/**
 * BaySelector Component
 * 
 * Main container for bay selection with loading, error, and empty states.
 * Renders the BayCarousel when bays are available.
 */

import { SnapCarousel } from '#components/data-display';
import { BayCard } from './BayCard';
import type { BaySelectorProps } from './types';

export function BaySelector({
  bays,
  selectedBayId,
  filterBayId,
  onBaySelect,
  isLoading = false,
  error = null,
  clubMessage,
  showAllBays = false,
  title = "Choose the bay you'd like to book",
  showNavigation = true,
  onBookByTime,
}: BaySelectorProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4 mt-4" aria-busy="true" aria-live="polite">
        <h2 className="text-xl font-semibold text-fg-primary">
          {title}
        </h2>
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="min-w-[280px] flex-shrink-0"
            >
              <div className="aspect-[4/3] animate-pulse rounded-2xl bg-gray-200" />
              <div className="mt-4 h-6 w-3/4 animate-pulse rounded bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="rounded-lg border-2 border-red-200 bg-red-50 p-6" role="alert">
        <div className="flex items-start gap-3">
          <svg
            className="h-6 w-6 flex-shrink-0 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="mb-1 text-sm font-semibold text-red-800">
              Unable to load bays
            </h3>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Check if there are any available bays (matching the carousel's filter logic)
  const availableBays = bays.filter(bay => {
    // If filtering by specific bay, check if it matches
    if (filterBayId !== null && filterBayId !== undefined && bay.id !== filterBayId) {
      return false;
    }
    // When showAllBays is true, skip availability check
    if (showAllBays) {
      return true;
    }
    // Only show available bays
    return bay.isAvailable;
  });
  
  // Empty state - no available bays for the selected time/filter
  if (availableBays.length === 0) {
    return (
      <div className="mt-10 rounded-lg border-2 border-fg-muted bg-bg-secondary p-8 text-center">
        <svg
          className="mx-auto h-12 w-12 text-fg-quaternary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <h3 className="mt-4 text-sm font-semibold text-fg-primary" data-testid="booking-bay-empty-state-title">
          No bays available
        </h3>
        <p className="mt-2 text-sm text-fg-quaternary">
          {clubMessage || 'All bays are booked for this time. Please try a different date or time.'}
        </p>
      </div>
    );
  }
  
  // Success state with bays — header + track now come from SnapCarousel (see README).
  return (
    <SnapCarousel
      title={title}
      titleTestId="booking-bay-selector-header"
      headerAction={
        onBookByTime && (
          <button
            type="button"
            onClick={onBookByTime}
            className="inline-flex h-7 items-center rounded-full border-1.25 border-theme-600 px-2.5 text-xs font-semibold text-butter-gray-600 text-interactive:bg-theme-400 text-interactive:text-white"
          >
            Book by Time
          </button>
        )
      }
      showNavigation={showNavigation}
      prevLabel="Scroll left to see more bays"
      nextLabel="Scroll right to see more bays"
      prevTestId="booking-bay-carousel-scroll-left"
      nextTestId="booking-bay-carousel-scroll-right"
      data-testid="bay-carousel"
      items={availableBays}
      getKey={(bay) => bay.id}
      renderItem={(bay, index) => (
        <BayCard
          bay={bay}
          isSelected={bay.id === selectedBayId}
          onSelect={onBaySelect}
          index={index}
          showAllBays={showAllBays}
        />
      )}
    />
  );
}
